import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  MapPin,
  Calendar,
  ArrowRight,
  FunnelSimple,
  CheckCircle,
  GraduationCap,
  FirstAid,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const categories = ["All", "TVET", "Enterprise", "Leadership", "Agriculture", "Technology", "Healthcare"];
const statuses = ["All", "ongoing", "completed", "planned"];

export default function ProjectsPage() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [selectedStatus, setSelectedStatus] = useState("All");

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/projects`);
        const data = await res.json();
        setProjects(data);
      } catch (err) {
        console.error("Error fetching projects:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchProjects();
  }, []);

  const filteredProjects = projects.filter((project) => {
    const categoryMatch = selectedCategory === "All" || project.category === selectedCategory;
    const statusMatch = selectedStatus === "All" || project.status === selectedStatus;
    return categoryMatch && statusMatch;
  });

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="relative py-20 md:py-28 bg-slate-50" data-testid="projects-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-3xl mx-auto"
          >
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-amber-600 mb-4 block">
              Our Projects
            </span>
            <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 tracking-tight mb-6">
              Programs & Initiatives
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed">
              Explore our ongoing and completed projects that are transforming communities across Africa through enterprise development, skills training, and community empowerment.
            </p>
          </motion.div>
        </div>
      </section>

      {/* TVET School Bombo - Full Fundraising Section */}
      <section className="py-16 md:py-24 bg-amber-500" data-testid="tvet-fundraising-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="flex items-center gap-3 mb-4">
                <GraduationCap className="h-8 w-8 text-green-950" weight="duotone" />
                <span className="px-3 py-1 rounded-full bg-green-700 text-white text-xs font-bold uppercase">
                  New Project
                </span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-green-950 tracking-tight mb-3">
                TVET School &ndash; Bombo
              </h2>
              <p className="text-lg font-semibold text-green-900 mb-4">
                Empowering Youth Through Skills &amp; Opportunity
              </p>
              <p className="text-base text-green-900/80 leading-relaxed mb-4">
                In Bombo, Luweero District, thousands of young people lack access to practical skills needed for employment and entrepreneurship. Many are eager to work, but lack the training and opportunities to succeed.
              </p>
              <p className="text-base text-green-900/80 leading-relaxed mb-6">
                <strong>PEDA AFRICA is changing that.</strong> We are establishing a modern TVET (Technical and Vocational Education &amp; Training) School that will equip youth with hands-on skills in:
              </p>
              
              <div className="grid sm:grid-cols-2 gap-2.5 mb-6">
                {["ICT & Digital Skills", "Carpentry & Construction", "Tailoring & Fashion Design", "Agribusiness & Farming", "Entrepreneurship & Business"].map((item) => (
                  <div key={item} className="flex items-center gap-2.5 bg-white/25 backdrop-blur rounded-xl p-3">
                    <CheckCircle className="h-5 w-5 text-green-950 shrink-0" weight="fill" />
                    <span className="text-green-950 text-sm font-medium">{item}</span>
                  </div>
                ))}
              </div>

              <div className="bg-white/20 backdrop-blur rounded-2xl p-5 mb-6">
                <p className="text-green-950 font-bold text-sm mb-2">This project will:</p>
                <ul className="space-y-1.5 text-sm text-green-900/90">
                  <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-green-800 shrink-0" weight="fill" /> Train 300+ youth annually</li>
                  <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-green-800 shrink-0" weight="fill" /> Reduce unemployment and poverty</li>
                  <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-green-800 shrink-0" weight="fill" /> Promote self-reliance and entrepreneurship</li>
                  <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-green-800 shrink-0" weight="fill" /> Strengthen local communities</li>
                </ul>
              </div>

              <p className="text-sm text-green-900/70 italic mb-6">Your support can build classrooms and training workshops, provide tools, machines, and equipment, and sponsor students from low-income families.</p>

              <div className="flex flex-col sm:flex-row gap-3">
                <Button asChild className="bg-green-700 hover:bg-green-800 text-white font-semibold rounded-xl px-6" data-testid="tvet-support-button">
                  <Link to="/donate">Support This Project <ArrowRight className="ml-2 h-4 w-4" weight="bold" /></Link>
                </Button>
                <Button asChild className="bg-white hover:bg-green-50 text-green-800 font-semibold rounded-xl px-6" data-testid="tvet-sponsor-button">
                  <Link to="/donate">Sponsor a Student</Link>
                </Button>
              </div>
            </motion.div>
            
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="grid grid-cols-2 gap-4">
              <img src="https://customer-assets.emergentagent.com/job_peda-enterprises/artifacts/afobkmcc_SCHOOL.png" alt="TVET School Bombo" className="w-full h-52 object-cover rounded-2xl shadow-lg col-span-2" />
              <img src="https://customer-assets.emergentagent.com/job_peda-enterprises/artifacts/0s4xigoz_ICT.png" alt="ICT Lab" className="w-full h-44 object-cover rounded-2xl shadow-lg" />
              <img src="https://customer-assets.emergentagent.com/job_peda-enterprises/artifacts/eghjahcl_PPD.png" alt="PEDA Production" className="w-full h-44 object-cover rounded-2xl shadow-lg" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Healthcare Center - Full Fundraising Section */}
      <section className="py-16 md:py-24 bg-green-950" data-testid="healthcare-fundraising-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="order-2 lg:order-1 grid grid-cols-2 gap-4">
              <img src="https://customer-assets.emergentagent.com/job_peda-enterprises/artifacts/fgzorv0v_HP.png" alt="Healthcare Center" className="w-full h-52 object-cover rounded-2xl shadow-xl col-span-2" />
              <img src="https://customer-assets.emergentagent.com/job_peda-enterprises/artifacts/qkndwzxh_NR.png" alt="Nursing Training" className="w-full h-44 object-cover rounded-2xl shadow-xl" />
              <img src="https://customer-assets.emergentagent.com/job_peda-enterprises/artifacts/93db3xb7_ppp.png" alt="PEDA Products" className="w-full h-44 object-cover rounded-2xl shadow-xl" />
            </motion.div>
            
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="order-1 lg:order-2">
              <div className="flex items-center gap-3 mb-4">
                <FirstAid className="h-8 w-8 text-amber-400" weight="duotone" />
                <span className="px-3 py-1 rounded-full bg-cyan-500 text-white text-xs font-bold uppercase animate-pulse">Coming Soon</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight mb-3">Community Healthcare Center &ndash; Bombo Town</h2>
              <p className="text-lg font-semibold text-amber-400 mb-4">Bringing Healthcare Closer to the Community</p>
              <p className="text-base text-green-100/80 leading-relaxed mb-4">In many parts of Bombo, access to quality healthcare remains limited. Families travel long distances for basic medical services, and preventive care is often unavailable.</p>
              <p className="text-base text-green-100/80 leading-relaxed mb-6"><strong className="text-white">PEDA AFRICA is launching a Community Healthcare Center to change this.</strong></p>
              
              <div className="grid sm:grid-cols-2 gap-2.5 mb-6">
                {["Primary healthcare services", "Maternal & child health support", "Preventive care & health education", "Basic diagnostics & treatment", "Community health outreach"].map((item) => (
                  <div key={item} className="flex items-center gap-2.5 bg-white/5 rounded-xl p-3">
                    <CheckCircle className="h-5 w-5 text-amber-400 shrink-0" weight="fill" />
                    <span className="text-green-100 text-sm">{item}</span>
                  </div>
                ))}
              </div>

              <div className="bg-white/5 rounded-2xl p-5 mb-6">
                <p className="text-white font-bold text-sm mb-2">This center will:</p>
                <ul className="space-y-1.5 text-sm text-green-100/80">
                  <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-amber-400 shrink-0" weight="fill" /> Serve thousands of residents annually</li>
                  <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-amber-400 shrink-0" weight="fill" /> Improve community health outcomes</li>
                  <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-amber-400 shrink-0" weight="fill" /> Support vulnerable populations</li>
                  <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-amber-400 shrink-0" weight="fill" /> Strengthen local healthcare systems</li>
                </ul>
              </div>

              <p className="text-sm text-green-200/60 italic mb-6">Healthy communities build strong economies. Your donation will help construct and equip the facility, provide medical supplies, and support healthcare training.</p>

              <div className="flex flex-col sm:flex-row gap-3">
                <Button asChild className="bg-amber-500 hover:bg-amber-600 text-green-950 font-semibold rounded-xl px-6" data-testid="healthcare-fund-button">
                  <Link to="/donate">Fund Healthcare Access <ArrowRight className="ml-2 h-4 w-4" weight="bold" /></Link>
                </Button>
                <Button asChild variant="outline" className="bg-white/10 hover:bg-white/20 border-white/25 text-white rounded-xl px-6" data-testid="healthcare-contact-button">
                  <Link to="/contact">Get In Touch</Link>
                </Button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="py-8 bg-white border-b border-slate-100" data-testid="projects-filters">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-center gap-4">
            <div className="flex items-center gap-2 text-slate-600">
              <FunnelSimple className="h-5 w-5" weight="duotone" />
              <span className="text-sm font-medium">Filter by:</span>
            </div>
            
            <div className="flex flex-wrap gap-2">
              <span className="text-sm text-slate-500 mr-2">Category:</span>
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-4 py-2 text-sm rounded-lg transition-colors ${
                    selectedCategory === category
                      ? "bg-green-700 text-white"
                      : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                  }`}
                  data-testid={`filter-category-${category.toLowerCase()}`}
                >
                  {category}
                </button>
              ))}
            </div>

            <div className="flex flex-wrap gap-2 md:ml-auto">
              <span className="text-sm text-slate-500 mr-2">Status:</span>
              {statuses.map((status) => (
                <button
                  key={status}
                  onClick={() => setSelectedStatus(status)}
                  className={`px-4 py-2 text-sm rounded-lg transition-colors capitalize ${
                    selectedStatus === status
                      ? "bg-amber-500 text-white"
                      : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                  }`}
                  data-testid={`filter-status-${status.toLowerCase()}`}
                >
                  {status}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="py-20 md:py-28" data-testid="projects-grid-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="bg-white rounded-2xl border border-slate-100 overflow-hidden animate-pulse">
                  <div className="aspect-[16/10] bg-slate-200" />
                  <div className="p-6 space-y-3">
                    <div className="flex gap-2">
                      <div className="h-6 w-16 bg-slate-200 rounded-full" />
                      <div className="h-6 w-20 bg-slate-200 rounded-full" />
                    </div>
                    <div className="h-5 w-3/4 bg-slate-200 rounded" />
                    <div className="h-4 w-full bg-slate-100 rounded" />
                    <div className="h-4 w-2/3 bg-slate-100 rounded" />
                  </div>
                </div>
              ))}
            </div>
          ) : filteredProjects.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-slate-600 mb-4">No projects found matching your criteria.</p>
              <Button 
                onClick={() => { setSelectedCategory("All"); setSelectedStatus("All"); }}
                variant="outline"
                className="rounded-xl"
              >
                Clear Filters
              </Button>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredProjects.map((project, index) => (
                <motion.article
                  key={project.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="group bg-white rounded-2xl border border-slate-100 overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300"
                  data-testid={`project-card-${project.id}`}
                >
                  <div className="aspect-[16/10] overflow-hidden relative">
                    <img 
                      src={project.image_url || "https://customer-assets.emergentagent.com/job_peda-enterprises/artifacts/afobkmcc_SCHOOL.png"} 
                      alt={project.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-4 left-4">
                      <span className={`px-3 py-1 text-xs font-medium rounded-full capitalize ${
                        project.status === "ongoing" 
                          ? "bg-green-500 text-white" 
                          : project.status === "completed"
                          ? "bg-slate-700 text-white"
                          : "bg-amber-500 text-white"
                      }`}>
                        {project.status}
                      </span>
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                        {project.category}
                      </span>
                    </div>
                    <h3 className="text-xl font-semibold text-slate-900 mb-3 group-hover:text-green-700 transition-colors">
                      {project.title}
                    </h3>
                    <p className="text-slate-600 text-sm leading-relaxed mb-4 line-clamp-3">
                      {project.description}
                    </p>
                    
                    <div className="pt-4 border-t border-slate-100 flex items-center justify-between">
                      {project.location && (
                        <div className="flex items-center gap-2 text-sm text-slate-500">
                          <MapPin className="h-4 w-4" weight="duotone" />
                          <span>{project.location}</span>
                        </div>
                      )}
                      {project.created_at && (
                        <div className="flex items-center gap-2 text-sm text-slate-500">
                          <Calendar className="h-4 w-4" weight="duotone" />
                          <span>{new Date(project.created_at).toLocaleDateString()}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </motion.article>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28 bg-green-950" data-testid="projects-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Partner With Us on Impact Projects
          </h2>
          <p className="text-lg text-green-100/80 mb-8 leading-relaxed">
            We are always looking for partners to collaborate on community development projects. Whether you're a donor, government agency, or organization, let's work together to create lasting change.
          </p>
          <Button 
            asChild 
            size="lg" 
            className="bg-amber-500 hover:bg-amber-600 text-green-950 font-semibold rounded-xl px-8"
            data-testid="projects-contact-button"
          >
            <Link to="/contact">
              Become a Partner <ArrowRight className="ml-2 h-4 w-4" weight="bold" />
            </Link>
          </Button>
        </div>
      </section>
    </div>
  );
}
